import '../../styles/fonts.css';
import React, { useState, useEffect, useRef } from 'react';

interface LoadingScreenProps {
  isVisible: boolean;
  onComplete?: () => void;
  loadingText?: string;
  minDuration?: number;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  isVisible,
  onComplete,
  loadingText = 'Загрузка...',
  minDuration = 5000,
}) => {
  const [searchProgress, setSearchProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState<string>(loadingText);

  const [imageSymbols] = useState([
    require('../../assets/crown.png'),
    require('../../assets/thunderball.png'),
    require('../../assets/ghost.png'),
    require('../../assets/heart.png'),
    require('../../assets/mario.png'),
    require('../../assets/hand.png'),
    require('../../assets/logo.png'),
  ]);

  const [slot1Offset, setSlot1Offset] = useState(0);
  const [slot2Offset, setSlot2Offset] = useState(0);
  const [slot3Offset, setSlot3Offset] = useState(0);
  const [slot1Stopped, setSlot1Stopped] = useState(false);
  const [slot2Stopped, setSlot2Stopped] = useState(false);
  const [slot3Stopped, setSlot3Stopped] = useState(false);

  const [slot1Queue, setSlot1Queue] = useState<string[]>(() => Array.from({ length: 20 }, () => getRandomSymbol(true)));
  const [slot2Queue, setSlot2Queue] = useState<string[]>(() => Array.from({ length: 20 }, () => getRandomSymbol(true)));
  const [slot3Queue, setSlot3Queue] = useState<string[]>(() => Array.from({ length: 20 }, () => getRandomSymbol(true)));

  const slot1IntervalRef = useRef<number | null>(null);
  const slot2IntervalRef = useRef<number | null>(null);
  const slot3IntervalRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<number | null>(null);

  function getRandomSymbol(excludeLogo = false): string {
    const pool = excludeLogo ? imageSymbols.filter((img) => img !== require('../../assets/logo.png')) : imageSymbols;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  const clearAllIntervals = () => {
    [slot1IntervalRef, slot2IntervalRef, slot3IntervalRef, progressIntervalRef].forEach((ref) => {
      if (ref.current) {
        clearInterval(ref.current);
        ref.current = null;
      }
    });
  };

  const startSlotAnimations = () => {
    const speed = 8;

    slot1IntervalRef.current = window.setInterval(() => {
      if (!slot1Stopped) {
        setSlot1Offset((prev) => {
          const next = prev + speed;
          if (next >= 96) {
            setSlot1Queue((prevQueue) => {
              const newQueue = [...prevQueue];
              newQueue.shift();
              newQueue.push(getRandomSymbol(true));
              return newQueue;
            });
            return next - 96;
          }
          return next;
        });
      }
    }, 16);

    slot2IntervalRef.current = window.setInterval(() => {
      if (!slot2Stopped) {
        setSlot2Offset((prev) => {
          const next = prev + speed;
          if (next >= 96) {
            setSlot2Queue((prevQueue) => {
              const newQueue = [...prevQueue];
              newQueue.shift();
              newQueue.push(getRandomSymbol(true));
              return newQueue;
            });
            return next - 96;
          }
          return next;
        });
      }
    }, 16);

    slot3IntervalRef.current = window.setInterval(() => {
      if (!slot3Stopped) {
        setSlot3Offset((prev) => {
          const next = prev + speed;
          if (next >= 96) {
            setSlot3Queue((prevQueue) => {
              const newQueue = [...prevQueue];
              newQueue.shift();
              newQueue.push(getRandomSymbol(true));
              return newQueue;
            });
            return next - 96;
          }
          return next;
        });
      }
    }, 16);
  };

  const stopSlot = (slotNumber: 1 | 2 | 3, finalSymbol?: string) => {
    if (slotNumber === 1) {
      setSlot1Queue((prev) => {
        const symbol = finalSymbol || prev[0];
        return [symbol, ...prev.slice(1)];
      });
      setSlot1Offset(0);
      setSlot1Stopped(true);
      if (slot1IntervalRef.current) {
        clearInterval(slot1IntervalRef.current);
        slot1IntervalRef.current = null;
      }
    } else if (slotNumber === 2) {
      setSlot2Queue((prev) => {
        const symbol = finalSymbol || require('../../assets/logo.png');
        return [symbol, ...prev.slice(1)];
      });
      setSlot2Offset(0);
      setSlot2Stopped(true);
      if (slot2IntervalRef.current) {
        clearInterval(slot2IntervalRef.current);
        slot2IntervalRef.current = null;
      }
    } else if (slotNumber === 3) {
      setSlot3Queue((prev) => {
        const symbol = finalSymbol || prev[0];
        return [symbol, ...prev.slice(1)];
      });
      setSlot3Offset(0);
      setSlot3Stopped(true);
      if (slot3IntervalRef.current) {
        clearInterval(slot3IntervalRef.current);
        slot3IntervalRef.current = null;
      }
    }
  };

  const getStatusMessage = (progress: number) => {
    if (progress < 20) return 'Инициализация...';
    if (progress < 40) return 'Подключение к серверу...';
    if (progress < 60) return 'Загрузка данных...';
    if (progress < 80) return 'Обработка информации...';
    if (progress < 95) return 'Завершение...';
    return 'Готово!';
  };

  const startProgressAnimation = () => {
    const startTime = Date.now();

    progressIntervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / minDuration) * 100, 100);

      setSearchProgress(Math.floor(progress));
      setCurrentStatus(getStatusMessage(progress));

      if (progress >= 25 && !slot1Stopped) {
        stopSlot(1);
      }
      if (progress >= 75 && !slot3Stopped) {
        stopSlot(3);
      }
      if (progress >= 90 && !slot2Stopped) {
        stopSlot(2);
      }

      if (progress >= 100) {
        setSearchProgress(100);
        setCurrentStatus('Готово!');

        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }

        setTimeout(() => {
          onComplete?.();
        }, 500);
      }
    }, 100);
  };

  const resetState = () => {
    setSearchProgress(0);
    setSlot1Offset(0);
    setSlot2Offset(0);
    setSlot3Offset(0);
    setSlot1Stopped(false);
    setSlot2Stopped(false);
    setSlot3Stopped(false);
    setCurrentStatus(loadingText);

    setSlot1Queue(Array.from({ length: 20 }, () => getRandomSymbol(true)));
    setSlot2Queue(Array.from({ length: 20 }, () => getRandomSymbol(true)));
    setSlot3Queue(Array.from({ length: 20 }, () => getRandomSymbol(true)));
  };

  useEffect(() => {
    if (isVisible) {
      resetState();
      clearAllIntervals();
      startSlotAnimations();
      startProgressAnimation();
    } else {
      clearAllIntervals();
    }

    return () => {
      clearAllIntervals();
    };
  }, [isVisible, minDuration]);

  if (!isVisible) return null;

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {/* Фоновый паттерн - такой же как в SearchScreen */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Crect width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='2.5' y='0' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='5' y='0' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='7.5' y='0' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='0' y='2.5' width='10' height='2.5' fill='%23ccc'/%3E%3Crect x='2.5' y='5' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='5' y='5' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='7.5' y='5' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='0' y='5' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='0' y='7.5' width='10' height='2.5' fill='%23ccc'/%3E%3C/svg%3E")`,
          backgroundSize: '10px 10px',
        }}
      />

      {/* Прогрессивная заливка */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Crect width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='2.5' y='0' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='5' y='0' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='7.5' y='0' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='0' y='2.5' width='10' height='2.5' fill='%23000'/%3E%3Crect x='2.5' y='5' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='5' y='5' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='7.5' y='5' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='0' y='5' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='0' y='7.5' width='10' height='2.5' fill='%23000'/%3E%3C/svg%3E")`,
          backgroundSize: '10px 10px',
          maskImage: `linear-gradient(to top, black ${searchProgress}%, transparent ${searchProgress}%)`,
          WebkitMaskImage: `linear-gradient(to top, black ${searchProgress}%, transparent ${searchProgress}%)`,
          transition: 'mask-image 150ms linear',
          zIndex: 2,
        }}
      />

      {/* Основной контент */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          {/* Слот-машина - точно такая же как в SearchScreen */}
          <div
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              marginBottom: '32px',
            }}
          >
            {/* Слот 1 */}
            <div
              style={{
                backgroundColor: '#e0e0e0',
                border: '4px solid #333',
                width: '96px',
                height: '96px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)',
              }}
            >
              <div style={{ overflow: 'hidden', height: '96px' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    transform: `translateY(-${slot1Offset}px)`,
                    transition: 'none',
                  }}
                >
                  {slot1Queue.map((img, index) => (
                    <div
                      key={index}
                      style={{
                        height: '96px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <img src={img} alt='' style={{ width: 64, height: 64 }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Слот 2 (центральный) */}
            <div
              style={{
                backgroundColor: slot2Stopped ? '#000' : '#e0e0e0',
                border: '4px solid #333',
                width: '96px',
                height: '96px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)',
              }}
            >
              <div style={{ overflow: 'hidden', height: '96px' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    transform: `translateY(-${slot2Offset}px)`,
                    transition: 'none',
                  }}
                >
                  {slot2Queue.map((img, index) => (
                    <div
                      key={index}
                      style={{
                        height: '96px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <img
                        src={img}
                        alt=''
                        style={{
                          width: img === require('../../assets/logo.png') ? 96 : 64,
                          height: img === require('../../assets/logo.png') ? 96 : 64,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Слот 3 */}
            <div
              style={{
                backgroundColor: '#e0e0e0',
                border: '4px solid #333',
                width: '96px',
                height: '96px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)',
              }}
            >
              <div style={{ overflow: 'hidden', height: '96px' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    transform: `translateY(-${slot3Offset}px)`,
                    transition: 'none',
                  }}
                >
                  {slot3Queue.map((img, index) => (
                    <div
                      key={index}
                      style={{
                        height: '96px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <img src={img} alt='' style={{ width: 64, height: 64 }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Статус */}
          <div
            style={{
              fontSize: '18px',
              fontFamily: 'monospace',
              marginBottom: '16px',
              color: searchProgress < 50 ? '#000' : '#fff',
              textShadow: searchProgress < 50 ? '1px 1px 2px rgba(255,255,255,0.5)' : '1px 1px 2px rgba(0,0,0,0.5)',
              transition: 'all 0.3s',
              minHeight: '22px',
            }}
          >
            {currentStatus}
          </div>

          {/* Прогресс */}
          <div
            style={{
              fontSize: '48px',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              color: searchProgress < 50 ? '#000' : '#fff',
              textShadow: searchProgress < 50 ? '2px 2px 4px rgba(255,255,255,0.5)' : '2px 2px 4px rgba(0,0,0,0.5)',
              transition: 'all 0.3s',
            }}
          >
            {searchProgress}%
          </div>
        </div>
      </div>
    </div>
  );
};
